import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router";
import { Box, Stack, useMediaQuery, useTheme } from "@mui/material";
import { Button, FoodsIcon, Typography } from "@foodpilot/foods";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { useGetAllTagTypes } from "src/api/client-api/tag/useGetAllTagTypes";
import { useGetAllTagValues } from "src/api/client-api/tag/useGetAllTagValues";
import { useTransformationReference } from "src/api/sure-api/useTransformationReference";
import { useDistributionReference } from "src/api/sure-api/useDistributionReference";
import { useConsumptionReference } from "src/api/sure-api/useConsumptionReference";
import { useFoodpilotContext } from "src/context/FoodpilotContext";
import { useProductMetadataContext } from "src/context/Product/Metadata/useProductMetadata";
import { ExtensionPoint, EXTENSION_POINTS } from "src/components/PuckExtensions";
import { useOrganizationSectionVisibility } from "src/pages/Product/hooks/useOrganizationSectionVisibility";
import { BrandCollectionStateError } from "src/api/client-api/brand/BrandCollectionStateError";
import { isBrandCollectionError } from "src/api/client-api/brand/useGetAllBrand";
import { PageBackground, SectionCard } from "src/components/EntityForm";
import { URLHandler } from "src/services/URLHandler";
import { resolveTagColors } from "src/components/TagManagement/tagColors";
import { ProductDetailPhoto } from "./ProductDetailPhoto";
import { formatWeightGrams, getPackagingDisplayName } from "./informationsDisplayHelpers";
import {
    CategoryTagList,
    DisplayTagList,
    InfoField,
    InformationDivider,
    InfoSection,
    productInformationNoValue as noValue,
    type DisplayTag,
} from "./ProductInformationDisplay";
import type { TagType, TagValue } from "src/types/tag";
import { JSON_ATTRIBUTE_ENTITY_CLASS } from "src/constants";

/**
 * Product informations v2 overview, aligned with Figma `13314:83194`.
 */
export const ProductInformationsV2 = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("md"));
    const navigate = useNavigate();
    const location = useLocation();
    const { product, productReference } = useProductMetadataContext();
    const { selectedCompany, entitiesJsonAttributes } = useFoodpilotContext();
    const { isFeatureActivated } = useApplicationParameters();

    const companyId = selectedCompany?.id ?? product.ownedBy?.id ?? 0;
    const {
        shouldHide: shouldHideOrganizationSection,
        hasProductLines,
        hasBrands,
        brandCollectionState,
        refetchBrands,
    } = useOrganizationSectionVisibility({
        currentCompanyId: companyId,
    });

    const productJsonAttributeDefinitions = entitiesJsonAttributes?.[JSON_ATTRIBUTE_ENTITY_CLASS.product] ?? [];
    const hasJsonAttributes = productJsonAttributeDefinitions.length > 0;
    const isProductDistributionEnabled = isFeatureActivated("FS-product-distribution");
    const isProductConsumptionEnabled = isFeatureActivated("FS-product-consumption");
    const isProductTransformationEnabled = isFeatureActivated("FS-product-transformation");
    const isPackagingEnabled = isFeatureActivated("FS-entity-packagings");
    const isIngredientsEnabled = isFeatureActivated("FS-entity-ingredients");

    const { tagTypes = [] } = useGetAllTagTypes({ context: "product", itemsPerPage: 100, active: true });
    const { data: tagValuesResponse } = useGetAllTagValues({ itemsPerPage: 1000 });
    const allTagValues: TagValue[] = useMemo(() => tagValuesResponse?.tagValues ?? [], [tagValuesResponse?.tagValues]);

    const { transformationReference } = useTransformationReference();
    const { distributionReference } = useDistributionReference();
    const { consumptionReference } = useConsumptionReference();

    const slugToValue = useMemo(
        () =>
            allTagValues.reduce<Record<string, TagValue>>((acc, value) => {
                acc[value.slug] = value;
                return acc;
            }, {}),
        [allTagValues],
    );

    const resolveTagValuesForType = (tagType: TagType): TagValue[] => {
        const selectedSlugs = product.tags?.[tagType.slug] ?? [];

        return selectedSlugs.reduce<TagValue[]>((values, slug) => {
            const tagValue = slugToValue[slug];
            if (tagValue) {
                values.push(tagValue);
            }
            return values;
        }, []);
    };

    const associatedEntityNames =
        product.shares
            ?.map((productCompany) => productCompany.organization.name)
            .filter((name) => name !== (product.ownedBy?.name ?? selectedCompany?.name)) ?? [];

    const transformationLabels =
        product.productTransformations?.map((transformation) => {
            const reference = transformationReference?.find(
                (item) => item.id === transformation.transformationReferenceId,
            );
            return reference?.label ?? noValue;
        }) ?? [];

    const distributionLabel =
        distributionReference?.find((item) => item.id === product.distributionReferenceId)?.label ??
        product.distribution?.name ??
        null;

    const consumptionLabels =
        product.productConsumptions?.map((productConsumption) => {
            const reference = consumptionReference?.find(
                (item) => item.id === productConsumption.consumptionReferenceId,
            );
            return (
                reference?.label ??
                productConsumption.consumptionNameFr ??
                productConsumption.consumptionCode ??
                noValue
            );
        }) ?? [];

    const netWeightLabel = formatWeightGrams(product.netWeight);

    const recipeIngredients: DisplayTag[] =
        product.productIngredients?.map((productIngredient) => {
            const ingredient = productIngredient.ingredient;
            const intermediateProduct = productIngredient.intermediateProduct;
            const name = ingredient?.name ?? intermediateProduct?.title ?? noValue;

            return {
                key: productIngredient["@id"],
                label: name,
                detail:
                    typeof productIngredient.usedQuantity === "number" ?
                        `(${productIngredient.usedQuantity} g)`
                    :   undefined,
            };
        }) ?? [];

    const packagingTags: DisplayTag[] =
        product.productPackagings?.map((productPackaging) => ({
            key: productPackaging["@id"],
            label: getPackagingDisplayName(productPackaging),
            detail:
                productPackaging.quantity === null || productPackaging.quantity === undefined ?
                    undefined
                :   `(${productPackaging.quantity})`,
        })) ?? [];

    const customTagItems = (tagType: TagType): DisplayTag[] =>
        resolveTagValuesForType(tagType).map((value) => {
            return {
                key: value.slug,
                label: value.name,
                colors: resolveTagColors(theme, tagType, value),
            };
        });

    const handleEditProduct = () => {
        if (selectedCompany === null || product.id === undefined) {
            return;
        }

        const currentUrl = location.pathname + location.search;
        const urlHandlerRoutes = product.isIntermediate === true ? URLHandler.intermediateProduct : URLHandler.product;
        const editUrl = urlHandlerRoutes.edit(selectedCompany.id, product.id);

        navigate(`${editUrl}?returnUrl=${encodeURIComponent(currentUrl)}&section=identity`);
    };

    return (
        <PageBackground>
            <Stack padding={theme.spacing(0, 0, 4)}>
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.PRODUCT_DETAIL_INFORMATION_HEADER} />
                <Stack
                    data-testid="product-information-header"
                    direction={isMobile ? "column" : "row"}
                    alignItems={isMobile ? "stretch" : "center"}
                    gap={theme.spacing(2)}
                    sx={{
                        marginBottom: theme.custom.strokes.containerDivider.width,
                        marginTop: `-${theme.custom.strokes.containerDivider.width}`,
                        paddingBlock: theme.spacing(2),
                    }}
                >
                    <ProductDetailPhoto product={product} />
                    <Stack flex={1} minWidth={0} gap={theme.spacing(0.5)}>
                        <Typography variant="h3">{product.title}</Typography>
                        <Stack direction="row" gap={theme.spacing(1)}>
                            <Typography variant="small-medium">
                                {t("product.v2.informations.owning_entity", "Owning entity:")}
                            </Typography>
                            <Typography variant="small-regular" sx={{ color: theme.custom.grey[2500] }}>
                                {product.ownedBy?.name ?? selectedCompany?.name ?? noValue}
                            </Typography>
                        </Stack>
                    </Stack>
                    {product.canModify !== false && (
                        <Button
                            variant="primary"
                            onClick={handleEditProduct}
                            startIcon={<FoodsIcon icon="edit" size={2} />}
                            sx={{
                                borderRadius: theme.spacing(1),
                                boxShadow: theme.custom.shadows.hard,
                                gap: theme.spacing(1),
                                height: theme.spacing(5),
                                paddingLeft: theme.spacing(2),
                                paddingRight: theme.spacing(2.5),
                                width: isMobile ? "100%" : undefined,
                                "& .MuiButton-startIcon": {
                                    margin: theme.spacing(0),
                                },
                            }}
                        >
                            {t("product.v2.informations.quick_edit", "Edit")}
                        </Button>
                    )}
                </Stack>

                <Box
                    data-testid="product-information-content"
                    sx={{
                        display: "grid",
                        gridTemplateColumns: isMobile ? "minmax(0, 1fr)" : "repeat(2, minmax(0, 1fr))",
                        alignItems: "start",
                        gap: theme.spacing(3),
                    }}
                >
                    <SectionCard data-testid="product-information-left-card">
                        <Stack padding={theme.spacing(3)} gap={theme.spacing(3)}>
                            <InfoSection
                                sectionKey="identity"
                                title={t("product.v2.informations.identity", "Identity")}
                            >
                                <InfoField
                                    label={`${t("product.v2.identity.unique_identifier", "Unique identifier")}:`}
                                    value={product.clientId}
                                />
                                <InfoField
                                    label={`${t("product.v2.identity.associated_reference_product", "Associated reference product")}:`}
                                    value={productReference?.name}
                                    tone="secondary"
                                />
                                <Box
                                    sx={{
                                        display: "grid",
                                        gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                        gap: theme.spacing(2),
                                    }}
                                >
                                    <InfoField
                                        label={`${t("product.v2.identity.organic_product", "Organic product")}:`}
                                        value={
                                            <Stack direction="row" alignItems="center" gap={theme.spacing(0.5)}>
                                                {product.organic === true && <FoodsIcon icon="check" size={2} />}
                                                <Typography
                                                    variant="body-regular"
                                                    sx={{ lineHeight: theme.spacing(2.75) }}
                                                >
                                                    {product.organic === true ?
                                                        t("common.yes", "Yes")
                                                    :   t("common.no", "No")}
                                                </Typography>
                                            </Stack>
                                        }
                                    />
                                    <InfoField
                                        label={`${t("product.v2.informations.weight", "Weight")}:`}
                                        value={netWeightLabel}
                                    />
                                </Box>
                            </InfoSection>

                            {hasJsonAttributes && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="additional"
                                        title={t("product.v2.additional_informations.title", "Additional informations")}
                                    >
                                        {productJsonAttributeDefinitions
                                            .filter((definition) => definition.canView !== false)
                                            .map((definition) => (
                                                <InfoField
                                                    key={definition.identifier}
                                                    label={`${definition.label || t("common.value", "Value")}:`}
                                                    value={String(
                                                        product.jsonAttributes?.[definition.identifier] ?? noValue,
                                                    )}
                                                />
                                            ))}
                                    </InfoSection>
                                </>
                            )}

                            {!shouldHideOrganizationSection && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="organization"
                                        title={t("product.v2.organization.section_title", "Organization")}
                                    >
                                        {(hasProductLines || hasBrands) && (
                                            <Box
                                                sx={{
                                                    display: "grid",
                                                    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                                                    gap: theme.spacing(2),
                                                }}
                                            >
                                                {hasProductLines && (
                                                    <InfoField
                                                        label={`${t("product.v2.informations.range", "Range")}:`}
                                                        value={product.productLine?.name}
                                                        tone="secondary"
                                                    />
                                                )}
                                                {!isBrandCollectionError(brandCollectionState) && hasBrands && (
                                                    <InfoField
                                                        label={`${t("product.identity.brand", "Brand")}:`}
                                                        value={product.brand?.name}
                                                        tone="secondary"
                                                    />
                                                )}
                                            </Box>
                                        )}
                                        {isBrandCollectionError(brandCollectionState) && (
                                            <BrandCollectionStateError
                                                state={brandCollectionState}
                                                onRetry={refetchBrands}
                                            />
                                        )}
                                        <InfoField
                                            label={t(
                                                "entity.organization.associated_entities.label",
                                                "Associated entities:",
                                            )}
                                            value={<CategoryTagList labels={associatedEntityNames} />}
                                        />
                                        {tagTypes.map((tagType) => (
                                            <InfoField
                                                key={tagType.id}
                                                label={`${tagType.name}:`}
                                                value={<DisplayTagList items={customTagItems(tagType)} />}
                                            />
                                        ))}
                                    </InfoSection>
                                </>
                            )}
                        </Stack>
                    </SectionCard>

                    <SectionCard data-testid="product-information-right-card">
                        <Stack padding={theme.spacing(3)} gap={theme.spacing(3)}>
                            {isIngredientsEnabled && (
                                <InfoSection
                                    sectionKey="recipe"
                                    title={`${t("product.recipe.title", "Recipe")} (${recipeIngredients.length})`}
                                >
                                    <DisplayTagList items={recipeIngredients} spacing="lifecycle" />
                                </InfoSection>
                            )}
                            {isProductTransformationEnabled && (
                                <>
                                    {isIngredientsEnabled && <InformationDivider />}
                                    <InfoSection
                                        sectionKey="transformation"
                                        title={t("product.v2.informations.processing", "Processing")}
                                    >
                                        <CategoryTagList labels={transformationLabels} spacing="lifecycle" />
                                    </InfoSection>
                                </>
                            )}
                            {isPackagingEnabled && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="packaging"
                                        title={`${t("product.v2.informations.packaging", "Packaging")} (${packagingTags.length})`}
                                    >
                                        <DisplayTagList items={packagingTags} spacing="lifecycle" />
                                    </InfoSection>
                                </>
                            )}
                            {isProductDistributionEnabled && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="distribution"
                                        title={t("product.distribution.title", "Distribution")}
                                    >
                                        <CategoryTagList
                                            labels={distributionLabel ? [distributionLabel] : []}
                                            spacing="lifecycle"
                                        />
                                    </InfoSection>
                                </>
                            )}
                            {isProductConsumptionEnabled && (
                                <>
                                    <InformationDivider />
                                    <InfoSection
                                        sectionKey="consumption"
                                        title={t("product.v2.informations.consumption", "Consumption")}
                                    >
                                        <CategoryTagList labels={consumptionLabels} spacing="lifecycle" />
                                    </InfoSection>
                                </>
                            )}
                        </Stack>
                    </SectionCard>
                </Box>
                <ExtensionPoint extensionPoint={EXTENSION_POINTS.PRODUCT_DETAIL_INFORMATION_FOOTER} />
            </Stack>
        </PageBackground>
    );
};
